import {
  Box,
  Flex,
  Group,
  Title,
  Text,
  Paper,
  Skeleton,
  alpha
} from '@mantine/core'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import { Calendar, Clock } from 'iconsax-reactjs'

import {
  articleEntranceTransition,
  slideUpAnimate,
  slideUpInitial
} from 'src/components/animations'
import { CategoryTag } from 'src/components/CategoryTag'
import { ProxiedImage } from 'src/components/ProxiedImage'
import { colors } from 'src/theme/colors'
import type { CompanyPulseItem } from 'src/types'
import { FORMAT_DATE_FULL, getReadTime } from 'src/utils'

type Props = {
  item?: CompanyPulseItem
  isPending: boolean
}

export const ArticleCenterHero = ({ item, isPending }: Props) => (
  <motion.div
    initial={slideUpInitial}
    animate={slideUpAnimate}
    transition={articleEntranceTransition}
  >
    <Paper
      radius="xl"
      h={{ base: '50vh', sm: '70vh' }}
      mih={{ base: 300, sm: 500 }}
      pos="relative"
      mb="xl"
      className="overflow-hidden"
    >
      {isPending ? (
        <Skeleton
          pos="absolute"
          style={{ inset: 0, zIndex: 1 }}
          h="100%"
          radius={0}
        />
      ) : (
        <>
          <Box pos="absolute" style={{ inset: 0 }}>
            <Box pos="absolute" style={{ inset: 0 }} bg="gray.2" />
            <ProxiedImage
              src={item?.hero_image?.url}
              transform={{ w: 1200 }}
              alt={item?.label}
              pos="absolute"
              w="100%"
              h="100%"
              fit="cover"
              style={{ inset: 0 }}
            />
            <Box
              pos="absolute"
              style={{
                inset: 0,
                background: `linear-gradient(to top, ${alpha(colors.black, 0.8)} 0%, ${alpha(colors.black, 0.4)} 50%, transparent 100%)`
              }}
            />
          </Box>

          <Flex
            pos="absolute"
            direction="column"
            justify="flex-end"
            p={{ base: 'md', md: 48 }}
            style={{ inset: 0 }}
          >
            <Group gap="xs" mb="lg">
              {item?.categories?.map((c) => (
                <CategoryTag
                  key={c?.id ?? c?.title}
                  label={c?.title ?? ''}
                  color={c?.color}
                  size="md"
                />
              ))}
            </Group>

            <Title
              order={1}
              c="white"
              mb="xl"
              maw={1200}
              style={{
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                lineHeight: 1.1
              }}
            >
              {item?.label}
            </Title>

            <Group gap="md">
              <Group gap={6}>
                <Calendar size={16} color={alpha(colors.white, 0.8)} />
                <Text size="sm" c="white">
                  {dayjs(item?.created_at).format(FORMAT_DATE_FULL)}
                </Text>
              </Group>
              <Group gap={6}>
                <Clock size={16} color={alpha(colors.white, 0.8)} />
                <Text size="sm" c="white">
                  {getReadTime(item?.content ?? item?.short_description ?? '')}
                </Text>
              </Group>
            </Group>
          </Flex>
        </>
      )}
    </Paper>
  </motion.div>
)
